import { DESCUENTO_DEL_REFERIDO_PORCENTAJE } from '@mc/socios'
import {
  AlertTriangle,
  CalendarCheck,
  CalendarDays,
  CalendarX,
  Contact,
  DoorOpen,
  ArrowRightLeft,
  Building2,
  Layers,
  ChevronRight,
  ChevronsUpDown,
  ClipboardList,
  CreditCard,
  FileText,
  FolderTree,
  Globe,
  HandCoins,
  History,
  Images,
  Inbox,
  Languages,
  Link2,
  ListTree,
  LogOut,
  Menu,
  Moon,
  Newspaper,
  Package,
  PanelLeftClose,
  PanelLeftOpen,
  ScrollText,
  Settings,
  ShoppingCart,
  Sparkles,
  Sun,
  Tags,
  Truck,
  UserCog,
  Users,
  Wallet,
  Wrench,
  FolderKanban,
  SlidersHorizontal,
  BookOpen,
  Briefcase,
  Building,
  Calendar,
  Car,
  FileStack,
  HeartPulse,
  House,
  Image,
  Map as Mapa,
  Shapes,
  Star,
  Tag,
  User,
  Utensils,
  SearchCheck,
  BarChart3,
  WandSparkles,
  type LucideIcon } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { Link, NavLink, Outlet, useLocation, useMatch, useNavigate, useParams } from 'react-router'

import { useInterruptores } from '../api/interruptores'
import { useMisCapacidades } from '../api/capacidades'
import { espaciosDe, useSalir, useSesion } from '../api/sesion'
import { useEspacio, useSaldo, useSitios } from '../api/consultas'
import { useTiposDeContenido } from '../api/estructura'
import { Aviso, BotonDeIcono, Cargando, clases } from '../componentes/ui'
import { BotonPublicarMiWeb } from '../publicar/BotonPublicarMiWeb'
import { useFichaDeAgencia, useMiProveedor } from '../api/agencia'
import { esDeAgencia, LogoDeMarca } from '../marca/marca'
import { ChipDeSocio } from '../socios/ChipDeSocio'
import { useAtribucion } from '../socios/useAtribucion'
import { seccionesDelEspacio, seccionesDelSitio, type Icono } from './secciones'

/**
 * El armazón del panel: un menú lateral blanco de 256 px con el espacio de trabajo
 * arriba, la web activa, las secciones con sus iconos y el usuario abajo; una cabecera
 * fina con la miga de pan; y el contenido sobre el lienzo gris, con su ancho acotado.
 *
 * El espacio y la web activos salen de **la dirección**, no de una cookie
 * (docs/panel-propio.md §5). Si no son suyos, la API responde 404 y aquí se dice en
 * llano: «no existe, o no es tuyo» (§5 de CLAUDE.md).
 *
 * El menú se pliega a iconos en escritorio (se recuerda) y es un cajón en móvil.
 */
const ICONOS: Record<Icono, LucideIcon> = {
  sitios: Globe,
  usuarios: Users,
  creditos: CreditCard,
  dominio: Link2,
  idiomas: Languages,
  google: SearchCheck,
  socios: HandCoins,
  auditoria: ScrollText,
  paginas: FileText,
  medios: Images,
  menus: ListTree,
  entradas: Newspaper,
  categorias: FolderTree,
  etiquetas: Tags,
  productos: Package,
  servicios: Wrench,
  'categorias-de-servicios': FolderKanban,
  pedidos: ShoppingCart,
  entregas: Truck,
  agenda: CalendarDays,
  reservas: CalendarCheck,
  recursos: DoorOpen,
  bloqueos: CalendarX,
  'clientes-de-agenda': Contact,
  pagos: Wallet,
  formularios: ClipboardList,
  respuestas: Inbox,
  redirecciones: ArrowRightLeft,
  'no-encontradas': AlertTriangle,
  generar: Sparkles,
  versiones: History,
  visitas: BarChart3,
  revision: WandSparkles,
  salud: HeartPulse,
  ajustes: Settings,
  campos: SlidersHorizontal,
  'tipos-de-contenido': Shapes,
  taxonomias: Tags,
  tipo: FileStack,
  'clientes-agencia': Building2,
  'planes-agencia': Layers,
}

/** El icono que eligió cada tipo de contenido (las opciones de su campo `icono` en el núcleo). Uno que no esté aquí, el genérico. */
const ICONOS_DE_TIPO: Record<string, LucideIcon> = {
  documento: FileText,
  casa: House,
  edificio: Building,
  persona: User,
  grupo: Users,
  etiqueta: Tag,
  calendario: Calendar,
  libro: BookOpen,
  herramienta: Wrench,
  estrella: Star,
  mapa: Mapa,
  foto: Image,
  maletin: Briefcase,
  coche: Car,
  comida: Utensils,
  salud: HeartPulse,
}

const CLAVE_PLEGADO = 'wixy-menu-plegado'

const iniciales = (texto: string) =>
  texto
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')

export const Marco = () => {
  const { espacio: espacioEnLaRuta } = useParams()
  const enUnSitio = useMatch('/e/:espacio/s/:sitio/*')
  // El constructor de un formulario (lienzo y barra de campos) aprovecha la pantalla ancha.
  const enElConstructor = useMatch('/e/:espacio/s/:sitio/formularios/:id')
  const espacio = Number(espacioEnLaRuta)
  const sitioActivo = Number(enUnSitio?.params.sitio) || null
  const { pathname } = useLocation()
  const navegar = useNavigate()

  const { usuario } = useSesion()
  const salir = useSalir()
  const { data: cuenta, isPending: cargandoEspacio, error } = useEspacio(espacio)
  const { data: sitios } = useSitios(espacio)
  const { data: saldo } = useSaldo(espacio)
  // Los tipos de contenido activos de la web: una entrada del menú por cada uno (plan del CMS dinámico §6).
  const sitioDelMenu = sitios?.find((s) => s.id === sitioActivo) ?? (sitios?.length === 1 ? (sitios[0] ?? null) : null)
  const { data: tipos } = useTiposDeContenido(sitioDelMenu?.id ?? null, Boolean(sitioDelMenu?.modulos?.includes('estructura')))
  const [abierto, setAbierto] = useState(false)
  const [plegado, setPlegado] = useState(() => {
    try {
      return localStorage.getItem(CLAVE_PLEGADO) === '1'
    } catch {
      return false
    }
  })

  // Al navegar en móvil, el cajón se cierra solo.
  useEffect(() => setAbierto(false), [pathname])

  // Lo que puede hacer quien mira en esta cuenta: lo dice el núcleo, y con ello se decide qué
  // puertas enseña el menú (las mismas capacidades con las que el núcleo protege).
  const permisos = useMisCapacidades(espacio)
  // Qué piezas del CMS dinámico están encendidas en esta instalación: lo apagado no sale en el menú.
  const interruptores = useInterruptores()
  // Si llegó por el enlace de un socio, la cuenta se le atribuye una vez (§30): es cosa del dueño.
  // Si este espacio es una agencia revendedora (§36), su menú lleva «Mis clientes» y «Mis planes».
  const { data: fichaDeAgencia } = useFichaDeAgencia(espacio)
  // En el panel de una agencia, las demás cuentas son sus clientes: pagan su plan a la agencia (§36, R5).
  const { data: miProveedor } = useMiProveedor(espacio, esDeAgencia() && fichaDeAgencia === null)
  const vinculada = useAtribucion(espacio, permisos.esDueno && !esDeAgencia())

  if (!Number.isInteger(espacio)) return <Aviso tono="error">Esa dirección no lleva a ningún espacio de trabajo.</Aviso>
  if (error) return <Aviso tono="error">Ese espacio de trabajo no existe, o no es tuyo.</Aviso>
  if (cargandoEspacio) return <Cargando que="Abriendo tu espacio" />

  const base = `/e/${espacio}`
  // Con una sola web no hay que elegirla (§4 de CLAUDE.md): sus secciones salen en el
  // menú desde el primer momento, aunque la dirección todavía no la nombre.
  const sitio = sitios?.find((s) => s.id === sitioActivo) ?? (sitios?.length === 1 ? (sitios[0] ?? null) : null)
  const secciones = [...seccionesDelEspacio({ base, permisos, agencia: fichaDeAgencia?.puede, pagos: Boolean(miProveedor) }), ...(sitio ? seccionesDelSitio({ base: `${base}/s/${sitio.id}`, permisos, sitio, tipos: tipos ?? [], interruptores }) : [])]
  const variosEspacios = usuario?.rol === 'superadmin' || espaciosDe(usuario).length > 1

  const plegar = () => {
    const siguiente = !plegado
    setPlegado(siguiente)
    try {
      localStorage.setItem(CLAVE_PLEGADO, siguiente ? '1' : '0')
    } catch {
      /* sin almacenamiento, vale para esta visita */
    }
  }

  // La miga: espacio › web › sección activa.
  const seccionActiva = secciones.flatMap((s) => s.enlaces).find((e) => (e.href === base ? pathname === base : pathname.startsWith(e.href)))
  const miga: Array<{ etiqueta: string; href?: string }> = [{ etiqueta: cuenta?.nombre ?? 'Espacio', href: base }]
  const delEspacio = ['usuarios', 'creditos', 'socios', 'auditoria', 'agencia'].some((tramo) => seccionActiva?.href.startsWith(`${base}/${tramo}`))
  if (sitio && seccionActiva && seccionActiva.href !== base && !delEspacio) {
    miga.push({ etiqueta: sitio.nombre, href: `${base}/s/${sitio.id}/paginas` })
  }
  if (seccionActiva && seccionActiva.href !== base) miga.push({ etiqueta: seccionActiva.etiqueta })

  const menu = (
    <>
      {/* Marca y plegado */}
      <div className={clases('flex h-14 shrink-0 items-center border-b border-linea', plegado ? 'justify-center px-2' : 'justify-between px-4')}>
        <Link className="flex items-center gap-2.5" to="/">
          <LogoDeMarca conNombre={!plegado} />
        </Link>
        {!plegado && (
          <BotonDeIcono className="hidden md:inline-flex" etiqueta="Plegar el menú" onClick={plegar} pequeno>
            <PanelLeftClose />
          </BotonDeIcono>
        )}
      </div>

      {/* El espacio de trabajo, y la web si hay varias */}
      {!plegado && (
        <div className="flex flex-col gap-2 border-b border-linea p-3">
          <Link
            className={clases(
              'flex items-center gap-2.5 rounded-boton border border-linea bg-papel-2 px-2.5 py-2 text-left',
              variosEspacios ? 'hover:border-linea-fuerte hover:bg-papel-3' : 'cursor-default',
            )}
            title={variosEspacios ? 'Cambiar de espacio de trabajo' : undefined}
            to={variosEspacios ? '/' : base}
          >
            <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-violeta-suave text-violeta">
              <Building2 className="size-4" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-medium">{cuenta?.nombre}</span>
              <span className="block truncate text-[11px] text-tenue">Espacio de trabajo</span>
            </span>
            {variosEspacios && <ChevronsUpDown className="size-3.5 shrink-0 text-tenue" />}
          </Link>
          {sitios && sitios.length > 1 && sitio && (
            <label className="relative block">
              <span className="sr-only">Web</span>
              <select
                className="campo w-full appearance-none rounded-boton border border-linea bg-papel py-2 pr-8 pl-2.5 text-[13px] font-medium"
                onChange={(e) => navegar(`${base}/s/${e.target.value}/paginas`)}
                value={sitio.id}
              >
                {sitios.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nombre}
                  </option>
                ))}
              </select>
              <ChevronsUpDown className="pointer-events-none absolute top-1/2 right-2.5 size-3.5 -translate-y-1/2 text-tenue" />
            </label>
          )}
        </div>
      )}

      {/* Las secciones */}
      <nav className={clases('flex-1 overflow-y-auto py-3', plegado ? 'px-2' : 'px-3')}>
        {secciones.map((seccion, i) => (
          <div className={clases(i > 0 && 'mt-4')} key={seccion.etiqueta}>
            {!plegado && <p className="mb-1 px-2.5 text-[11px] font-semibold tracking-wider text-tenue uppercase">{seccion.etiqueta}</p>}
            {plegado && i > 0 && <div className="mx-2 mb-2 border-t border-linea" />}
            <ul className="flex flex-col gap-0.5">
              {seccion.enlaces.map((enlace) => {
                const Icono = enlace.icono === 'tipo' ? (ICONOS_DE_TIPO[enlace.variante ?? ''] ?? ICONOS.tipo) : ICONOS[enlace.icono]
                return (
                  <li key={enlace.href}>
                    <NavLink
                      className={({ isActive }) =>
                        clases(
                          'group flex items-center gap-2.5 rounded-boton text-[13.5px] transition-colors',
                          plegado ? 'justify-center px-0 py-2' : 'px-2.5 py-[7px]',
                          isActive ? 'bg-violeta-suave font-medium text-violeta' : 'text-tinta-2 hover:bg-papel-3 hover:text-tinta',
                        )
                      }
                      end={enlace.href === base}
                      title={plegado ? enlace.etiqueta : undefined}
                      to={enlace.href}
                    >
                      {({ isActive }) => (
                        <>
                          <Icono className={clases('size-4 shrink-0', isActive ? 'text-violeta' : 'text-tenue group-hover:text-tinta-2')} />
                          {!plegado && <span className="truncate">{enlace.etiqueta}</span>}
                        </>
                      )}
                    </NavLink>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* El usuario */}
      <div className={clases('border-t border-linea p-3', plegado && 'flex flex-col items-center gap-2 px-2')}>
        {plegado ? (
          <>
            <BotonDeIcono etiqueta="Desplegar el menú" onClick={plegar} pequeno>
              <PanelLeftOpen />
            </BotonDeIcono>
            <BotonDeTema soloIcono />
            <BotonDeIcono etiqueta="Mi cuenta" onClick={() => navegar(`/e/${espacio}/cuenta`)} pequeno>
              <UserCog />
            </BotonDeIcono>
            <BotonDeIcono etiqueta="Salir" onClick={() => salir.mutate()} pequeno>
              <LogOut />
            </BotonDeIcono>
          </>
        ) : (
          <div className="flex items-center gap-2.5">
            {/* Tu nombre lleva a «Mi cuenta»: el correo, la contraseña y el nombre (paginas/MiCuenta.tsx). */}
            <Link className="-m-1 flex min-w-0 flex-1 items-center gap-2.5 rounded-boton p-1 hover:bg-papel-2" title="Mi cuenta: correo y contraseña" to={`/e/${espacio}/cuenta`}>
              <span aria-hidden className="flex size-8 shrink-0 items-center justify-center rounded-full bg-papel-3 text-[11px] font-semibold text-tinta-2">
                {iniciales(usuario?.nombre || usuario?.email || '?')}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-medium">{usuario?.nombre || usuario?.email}</span>
                <span className="block truncate text-[11px] text-tenue">{usuario?.nombre ? usuario.email : (usuario?.rol ?? '')}</span>
              </span>
            </Link>
            <BotonDeTema soloIcono />
            <BotonDeIcono etiqueta="Salir" onClick={() => salir.mutate()} pequeno>
              <LogOut />
            </BotonDeIcono>
          </div>
        )}
      </div>
    </>
  )

  return (
    <div className="flex min-h-full">
      <a className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded focus:bg-papel focus:p-2" href="#contenido">
        Saltar al contenido
      </a>

      {/* El cajón en móvil */}
      {abierto && <button aria-label="Cerrar el menú" className="fixed inset-0 z-20 bg-velo/40 md:hidden" onClick={() => setAbierto(false)} type="button" />}
      <aside
        className={clases(
          'fixed inset-y-0 left-0 z-30 flex flex-col border-r border-linea bg-papel transition-transform md:sticky md:top-0 md:h-screen md:translate-x-0',
          plegado ? 'w-16' : 'w-64',
          abierto ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {menu}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* La cabecera: la miga, y en móvil el botón del menú */}
        <header className="sticky top-0 z-10 flex h-14 shrink-0 items-center gap-3 border-b border-linea bg-papel/80 px-4 backdrop-blur md:px-8">
          <BotonDeIcono className="md:hidden" etiqueta="Abrir el menú" onClick={() => setAbierto(true)}>
            <Menu />
          </BotonDeIcono>
          <nav aria-label="Dónde estás" className="flex min-w-0 items-center gap-1 text-[13px]">
            {miga.map((tramo, i) => (
              <span className="flex min-w-0 items-center gap-1" key={`${tramo.etiqueta}-${i}`}>
                {i > 0 && <ChevronRight aria-hidden className="size-3.5 shrink-0 text-tenue" />}
                {tramo.href && i < miga.length - 1 ? (
                  <Link className="truncate text-tenue hover:text-tinta" to={tramo.href}>
                    {tramo.etiqueta}
                  </Link>
                ) : (
                  <span className={clases('truncate', i === miga.length - 1 ? 'font-medium text-tinta' : 'text-tenue')}>{tramo.etiqueta}</span>
                )}
              </span>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            {/* «Publicar mi web»: con una web activa, para quien publica contenido (el autor publica lo suyo desde su editor). */}
            {sitio && permisos.tiene('contenido.publicar') && <BotonPublicarMiWeb espacio={espacio} key={sitio.id} sitio={sitio.id} />}
            {permisos.esDueno && !esDeAgencia() && <ChipDeSocio espacio={espacio} />}
            {saldo && permisos.tiene('creditos.ver') && (
              <Link className="hidden items-center gap-1.5 rounded-boton border border-linea px-2.5 py-1.5 text-[13px] hover:bg-papel-2 sm:inline-flex" title="Créditos disponibles" to={`${base}/creditos`}>
                <Sparkles className="size-3.5 text-violeta" />
                <span className="font-medium">{saldo.total.toLocaleString('es')}</span>
                <span className="text-tenue">créditos</span>
              </Link>
            )}
          </div>
        </header>

        {/* Abajo, sitio de sobra para «Ayuda», que flota en la esquina (componentes/Ayuda.tsx): con
            el relleno de antes, al bajar del todo tapaba lo último de la pantalla —el «Continuar»
            del asistente de alta— y no se podía pulsar. */}
        <main className={clases('mx-auto w-full flex-1 px-4 pt-7 pb-20 md:px-8', enElConstructor ? 'max-w-[1560px]' : 'max-w-[1200px]')} id="contenido">
          {vinculada && (
            <div className="mb-6">
              <Aviso tono="ok">Tu cuenta ha quedado vinculada al socio que te recomendó: tu primer pago lleva un {DESCUENTO_DEL_REFERIDO_PORCENTAJE} % de descuento.</Aviso>
            </div>
          )}
          <Outlet context={{ espacio, sitio, saldo: saldo?.total ?? null, permisos }} />
        </main>
      </div>
    </div>
  )
}

const BotonDeTema = ({ soloIcono = false }: { soloIcono?: boolean }) => {
  const [oscuro, setOscuro] = useState(() => document.documentElement.dataset.tema === 'oscuro')
  const cambiar = () => {
    const siguiente = !oscuro
    document.documentElement.dataset.tema = siguiente ? 'oscuro' : 'claro'
    try {
      localStorage.setItem('wixy-tema', siguiente ? 'oscuro' : 'claro')
    } catch {
      /* sin almacenamiento, vale para esta visita */
    }
    setOscuro(siguiente)
  }
  const etiqueta = oscuro ? 'Pasar a modo claro' : 'Pasar a modo oscuro'
  const icono: ReactNode = oscuro ? <Sun /> : <Moon />
  return (
    <BotonDeIcono etiqueta={etiqueta} onClick={cambiar} pequeno={soloIcono}>
      {icono}
    </BotonDeIcono>
  )
}
